import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import StackedBarChart from '../components/charts/StackedBarChart'
import HorizontalBarChart from '../components/charts/HorizontalBarChart'
import BarChart from '../components/charts/BarChart'
import {
  UserPlusIcon,
  CogIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatMonth,
  formatDate,
  getCountryName
} from '../utils/formatters'

const ONBOARDING_STAGES = ['New Client', 'System Set Up', 'Roll Out', 'Active Usage', 'Done']

export default function OnboardingDashboard() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const start = startMonth || months[Math.max(0, months.length - 9)]
    const end = endMonth || months[months.length - 1]
    const filteredMonths = months.filter(m => m >= start && m <= end)

    // Get current onboarding records (where current_stage is true or "True")
    let onboarding = data.onboarding.filter(o =>
      o.current_stage === true || o.current_stage === 'True'
    )

    if (selectedCountry) {
      onboarding = onboarding.filter(o => o.country === selectedCountry)
    }

    // Current stage counts
    const stageCounts = ONBOARDING_STAGES.reduce((acc, stage) => {
      acc[stage] = onboarding.filter(o => o.onboarding_stage === stage).length
      return acc
    }, {})

    // In progress (not Done)
    const inProgress = onboarding.filter(o => o.onboarding_stage !== 'Done')
    const completed = onboarding.filter(o => o.onboarding_stage === 'Done')

    // Total ARR in onboarding
    const totalArrInOnboarding = inProgress.reduce((sum, o) => sum + (o.arr_value || 0), 0)

    // Onboarding time distribution
    const timeDistribution = {}
    completed.forEach(o => {
      const weeks = o.onboarding_weeks || 0
      let bucket
      if (weeks <= 1) bucket = '<1 wk'
      else if (weeks <= 2) bucket = '1-2 wks'
      else if (weeks <= 3) bucket = '2-3 wks'
      else if (weeks <= 4) bucket = '3-4 wks'
      else if (weeks <= 5) bucket = '4-5 wks'
      else if (weeks <= 6) bucket = '5-6 wks'
      else if (weeks <= 8) bucket = '7-8 wks'
      else bucket = '8+ wks'

      timeDistribution[bucket] = (timeDistribution[bucket] || 0) + 1
    })

    const timeDistributionData = [
      '<1 wk', '1-2 wks', '2-3 wks', '3-4 wks', '4-5 wks', '5-6 wks', '7-8 wks', '8+ wks'
    ].map(label => ({
      label,
      value: timeDistribution[label] || 0
    }))

    // Pipeline by month and stage
    const pipelineByMonth = ONBOARDING_STAGES.slice(0, -1).map(stage => ({
      label: stage,
      data: filteredMonths.map(month => {
        return data.onboarding.filter(o => {
          const startMonth = o.stage_start_date?.substring(0, 7)
          return startMonth === month && o.onboarding_stage === stage
        }).length
      })
    }))

    // Pipeline value by month
    const pipelineValueByMonth = ONBOARDING_STAGES.slice(0, -1).map(stage => ({
      label: stage,
      data: filteredMonths.map(month => {
        const stageRecords = data.onboarding.filter(o => {
          const startMonth = o.stage_start_date?.substring(0, 7)
          return startMonth === month && o.onboarding_stage === stage
        })
        return stageRecords.reduce((sum, o) => sum + (o.arr_value || 0), 0)
      })
    }))

    // By country comparison
    const byCountry = computed.countries.map(country => {
      const countryRecords = onboarding.filter(o => o.country === country)
      return {
        country,
        countryName: getCountryName(country),
        inOnboarding: countryRecords.filter(o => o.onboarding_stage !== 'Done').length,
        completed: countryRecords.filter(o => o.onboarding_stage === 'Done').length
      }
    }).filter(c => c.inOnboarding > 0 || c.completed > 0)

    // Table data for active onboarding
    const tableData = inProgress.map(o => ({
      ...o,
      id: o.onboarding_id
    }))

    return {
      months: filteredMonths,
      stageCounts,
      inProgress,
      completed,
      totalArrInOnboarding,
      timeDistributionData,
      pipelineByMonth,
      pipelineValueByMonth,
      byCountry,
      tableData
    }
  }, [data, computed, selectedCountry, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    stageCounts,
    inProgress,
    completed,
    totalArrInOnboarding,
    timeDistributionData,
    pipelineByMonth,
    pipelineValueByMonth,
    byCountry,
    tableData
  } = filteredData

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <FilterDropdown
          label="Country"
          value={selectedCountry}
          options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
          onChange={setSelectedCountry}
          placeholder="All Countries"
        />
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <KPICard
          title="New Clients"
          value={stageCounts['New Client']?.toLocaleString()}
          icon={UserPlusIcon}
        />
        <KPICard
          title="System Set Ups"
          value={stageCounts['System Set Up']?.toLocaleString()}
          icon={CogIcon}
        />
        <KPICard
          title="Roll Outs"
          value={stageCounts['Roll Out']?.toLocaleString()}
          icon={RocketLaunchIcon}
        />
        <KPICard
          title="Active Usage"
          value={stageCounts['Active Usage']?.toLocaleString()}
          icon={CheckCircleIcon}
          colorScheme="success"
        />
        <KPICard
          title="Total in Onboarding"
          value={formatCurrency(totalArrInOnboarding, { compact: true })}
          subtitle={`${inProgress.length} customers`}
          icon={CurrencyDollarIcon}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Pipeline Development" subtitle="Totals by stage" height="h-64">
          <StackedBarChart
            labels={months}
            datasets={pipelineByMonth}
            yAxisPrefix=""
          />
        </ChartCard>

        <ChartCard title="Pipeline Development" subtitle="ARR value by stage" height="h-64">
          <StackedBarChart
            labels={months}
            datasets={pipelineValueByMonth}
          />
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Onboarding Time Distribution" subtitle="Completed customers by weeks" height="h-64">
          <HorizontalBarChart
            data={timeDistributionData}
            valueKey="value"
            labelKey="label"
          />
        </ChartCard>

        <ChartCard title="Customers in Onboarding" subtitle="By country" height="h-64">
          <BarChart
            labels={byCountry.map(c => c.countryName)}
            datasets={[
              { label: 'In Onboarding', data: byCountry.map(c => c.inOnboarding), color: '#3b82f6' },
              { label: 'Completed', data: byCountry.map(c => c.completed), color: '#22c55e' }
            ]}
            formatLabels={false}
            yAxisPrefix=""
          />
        </ChartCard>
      </div>

      {/* Active Onboarding Table */}
      <ChartCard title="Active Onboarding Customers" height="h-auto">
        <DataTable
          columns={[
            { key: 'customer_id', header: 'Customer', render: (v, row) => (
              <span className="text-brand-600 dark:text-brand-400 font-medium">
                {v}
              </span>
            )},
            { key: 'organization_name', header: 'Organization' },
            { key: 'country', header: 'Country', render: v => getCountryName(v) },
            { key: 'subscription_type', header: 'Subscription' },
            { key: 'arr_value', header: 'ARR', render: v => formatCurrency(v) },
            { key: 'onboarding_stage', header: 'Stage', render: v => (
              <span className={`badge ${
                v === 'New Client' ? 'badge-info' :
                v === 'System Set Up' ? 'badge-warning' :
                v === 'Roll Out' ? 'badge-info' :
                'badge-success'
              }`}>
                {v}
              </span>
            )},
            { key: 'stage_start_date', header: 'Stage Start', render: v => formatDate(v) },
            { key: 'onboarding_weeks', header: 'Est. Weeks' }
          ]}
          data={tableData}
          pageSize={10}
          searchable={true}
          searchPlaceholder="Search customers..."
        />
      </ChartCard>
    </div>
  )
}
